import React, { useState, useEffect } from 'react';
import { Building2, Users, UserCheck, Building, Download, RefreshCw } from 'lucide-react';
import { useOrganigramme } from '../../hooks/useOrganigramme';
import { useSites } from '../../hooks/useSites';
import { useDepartments } from '../../hooks/useDepartments';
import { Organigramme, OrganigrammeFilters } from '../../../../types/organisation';
import Button from '../../../../components/ui/Button';
import Select from '../../../../components/ui/Select';
import DateInput from '../../../../components/ui/DateInput';
import OrganigrammePyramid from './OrganigrammePyramid';
import OrganigrammeAllSites from './OrganigrammeAllSites';
import OrganisationNavigation from '../OrganisationNavigation';

const OrganigrammeView: React.FC = () => {
  const [selectedOrganigramme, setSelectedOrganigramme] = useState<Organigramme | null>(null);
  const [filters, setFilters] = useState<OrganigrammeFilters>({});

  const {
    organigrammes,
    loading,
    error,
    fetchOrganigrammes,
    fetchOrganigrammeBySite,
    fetchOrganigrammeByDepartment,
  } = useOrganigramme();

  const { sites, loading: sitesLoading } = useSites();
  const { departments, loading: departmentsLoading } = useDepartments();

  // Charger automatiquement "Tous les sites" au montage du composant
  useEffect(() => {
    if (!filters.siteId && !filters.departmentId && !filters.showAllSites) {
      setFilters(prev => ({
        ...prev,
        showAllSites: true,
      }));
    }
  }, []);

  useEffect(() => {
    fetchOrganigrammes(filters.date);
  }, [filters.date]);

  useEffect(() => {
    if (filters.siteId) {
      fetchOrganigrammeBySite(filters.siteId, filters.date).then(setSelectedOrganigramme);
    } else if (filters.departmentId) {
      fetchOrganigrammeByDepartment(filters.departmentId, filters.date).then(setSelectedOrganigramme);
    } else if (filters.showAllSites) {
      fetchOrganigrammes(filters.date).then(() => {
        // Pour l'affichage de tous les sites, on prend le premier organigramme comme exemple
        // ou on affiche une vue consolidée
        setSelectedOrganigramme(null);
      });
    } else {
      setSelectedOrganigramme(null);
    }
  }, [filters.siteId, filters.departmentId, filters.date, filters.showAllSites]);

  const handleSiteChange = (siteId: string) => {
    setFilters(prev => ({
      ...prev,
      siteId: siteId && siteId !== 'all' ? parseInt(siteId) : undefined,
      departmentId: undefined, // Reset department when site changes
      showAllSites: siteId === 'all',
    }));
  };

  const handleDepartmentChange = (departmentId: string) => {
    setFilters(prev => ({
      ...prev,
      departmentId: departmentId ? parseInt(departmentId) : undefined,
      siteId: undefined, // Reset site when department changes
    }));
  };

  const handleDateChange = (date: string) => {
    setFilters(prev => ({ ...prev, date }));
  };


  const handleExport = () => {
    if (!selectedOrganigramme) return;
    
    const data = {
      site: selectedOrganigramme.site,
      nodes: selectedOrganigramme.nodes,
      edges: selectedOrganigramme.edges,
      stats: selectedOrganigramme.stats,
      exportedAt: new Date().toISOString(),
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `organigramme-${selectedOrganigramme.site.siteCode}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleRefresh = () => {
    if (filters.siteId) {
      fetchOrganigrammeBySite(filters.siteId, filters.date).then(setSelectedOrganigramme);
    } else if (filters.departmentId) {
      fetchOrganigrammeByDepartment(filters.departmentId, filters.date).then(setSelectedOrganigramme);
    } else {
      fetchOrganigrammes(filters.date);
    }
  };

  return (
    <div className="space-y-6">
      <OrganisationNavigation />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Organigramme</h1>
          <p className="text-gray-600">Visualisation de la hiérarchie organisationnelle</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={loading}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
          <Button
            variant="outline"
            onClick={handleExport}
            disabled={!selectedOrganigramme}
            className="flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            Exporter
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Select
              label="Site"
              value={filters.showAllSites ? 'all' : (filters.siteId?.toString() || '')}
              onChange={handleSiteChange}
              options={[
                { value: '', label: 'Sélectionner un site' },
                { value: 'all', label: 'Tous les sites' },
                ...(sites || []).map((site: { id: number; siteName: string; siteCode: string }) => ({
                  value: site.id.toString(),
                  label: site.siteName,
                })),
              ]}
              disabled={sitesLoading}
            />
          </div>
          <div className="flex-1">
            <Select
              label="Département"
              value={filters.departmentId?.toString() || ''}
              onChange={handleDepartmentChange}
              options={[
                { value: '', label: 'Tous les départements' },
                ...(departments || []).map((dept: { id: number; departmentName: string; departmentCode: string }) => ({
                  value: dept.id.toString(),
                  label: dept.departmentName,
                })),
              ]}
              disabled={departmentsLoading}
            />
          </div>
          <div className="flex-1">
            <DateInput
              label="Date d'effet"
              value={filters.date || ''}
              onChange={handleDateChange}
            />
          </div>
        </div>
      </div>

      {/* Stats */}
      {selectedOrganigramme && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-600 truncate">Employés</p>
                <p className="text-xl font-semibold text-gray-900 truncate">
                  {selectedOrganigramme.stats.totalEmployees}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg flex-shrink-0">
                <UserCheck className="h-5 w-5 text-green-600" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-600 truncate">Managers</p>
                <p className="text-xl font-semibold text-gray-900 truncate">
                  {selectedOrganigramme.stats.totalManagers}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg flex-shrink-0">
                <Building className="h-5 w-5 text-purple-600" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-600 truncate">Départements</p>
                <p className="text-xl font-semibold text-gray-900 truncate">
                  {selectedOrganigramme.stats.totalDepartments}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg flex-shrink-0">
                <Building2 className="h-5 w-5 text-orange-600" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-600 truncate">Positions</p>
                <p className="text-xl font-semibold text-gray-900 truncate">
                  {selectedOrganigramme.stats.totalPositions}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Organigramme */}
      <div className="bg-white rounded-lg border border-gray-200">
        {loading ? (
          <div className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Chargement de l'organigramme...</span>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={handleRefresh}>Réessayer</Button>
          </div>
        ) : selectedOrganigramme ? (
          <OrganigrammePyramid
            organigramme={selectedOrganigramme}
          />
          ) : filters.showAllSites && organigrammes.length > 0 ? (
            <OrganigrammeAllSites organigrammes={organigrammes} />
        ) : (
          <div className="p-8 text-center text-gray-500">
            <Building2 className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p>Sélectionnez un site ou un département pour visualiser l'organigramme</p>
          </div>
        )}
      </div>

    </div>
  );
};

export default OrganigrammeView;

